import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies & Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-contrast-drop-line',
  'Pants & Shorts': 'ri-contrast-2-line',
  'Headwear': 'ri-base-station-line',
  'Sets': 'ri-stack-line',
  'Unterwäsche': 'ri-heart-line',
  'Jersey': 'ri-shirt-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-briefcase-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  const handleMobileMenuClick = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
            onClick={handleMobileMenuClick}
          >
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 tracking-tight whitespace-nowrap">
              OOPBuy <span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-4-line"></i>
              </span>
              Startseite
            </Link>

            <Link
              to="/oopbuy-spreadsheet-guide"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Kategorien Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line"></i>
                </span>
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-3 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/oopbuy-spreadsheet-sicherheit"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav_security')}
            </Link>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2.5">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium border border-foreground-300 text-foreground-800 rounded-full hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Zur Hauptwebsite
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl text-foreground-800 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-5 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            {/* Main Links */}
            <Link
              to="/"
              onClick={handleMobileMenuClick}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-4-line text-primary-500"></i>
              </span>
              <div>
                <span className="font-medium">Startseite</span>
                <span className="block text-xs text-foreground-500 font-normal">Zurück zur Übersicht</span>
              </div>
            </Link>

            <Link
              to="/oopbuy-spreadsheet-guide"
              onClick={handleMobileMenuClick}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-accent-600"></i>
              </span>
              <div>
                <span className="font-medium">Shopping Guide</span>
                <span className="block text-xs text-foreground-500 font-normal">Komplette Anleitung</span>
              </div>
            </Link>

            <Link
              to="/oopbuy-spreadsheet-sicherheit"
              onClick={handleMobileMenuClick}
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-secondary-600"></i>
              </span>
              <div>
                <span className="font-medium">Sicherheit</span>
                <span className="block text-xs text-foreground-500 font-normal">Tipps & Schutz</span>
              </div>
            </Link>

            {/* Categories Toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer w-full"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-grid-line text-foreground-600"></i>
                </span>
                <div className="text-left">
                  <span className="font-medium">Kategorien</span>
                  <span className="block text-xs text-foreground-500 font-normal">14 Produktkategorien</span>
                </div>
              </div>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''} text-foreground-400`}></i>
            </button>

            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/70 grid grid-cols-1 gap-1 mt-1 mb-2">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors text-left cursor-pointer w-full"
                  >
                    <span className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </span>
                    <span>{cat.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* SEO Articles */}
            <div className="mt-2 pt-3 border-t border-background-200/70">
              <p className="px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">SEO Ratgeber</p>
              <div className="flex flex-col gap-1">
                {articles.slice(0, 5).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    onClick={handleMobileMenuClick}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-article-line text-xs text-foreground-400"></i>
                    </span>
                    <span className="truncate">{article.title}</span>
                  </Link>
                ))}
                {articles.slice(5).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    onClick={handleMobileMenuClick}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-article-line text-xs text-foreground-400"></i>
                    </span>
                    <span className="truncate">{article.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile CTAs */}
            <div className="border-t border-background-200/70 pt-4 mt-3 flex flex-col gap-2.5">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-medium text-center border border-foreground-300 text-foreground-800 rounded-full hover:bg-foreground-950 hover:text-background-50 transition-all cursor-pointer"
              >
                Zur Hauptwebsite
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-medium text-center bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}